import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CopyAllIcon from "@mui/icons-material/CopyAll";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import * as constants from "../../../../constants";
import ContainedButton from "../../../../shared/Button/CustomContainedButton";
import { RootState } from "../../../../store/store";
import basestyle from "../../../CSS/Base.module.css";
import { getBlackBaudDetailsBytransactionDateAndClientId, postBlackBaudTransaction } from "../../services/BlackBaud/BlackBaudServices";
import {
  SectionTitle,
  StyledOneChildContainer,
  StyledParentContainer,
  StyledTwoChildContainer,
} from "../../StyledComponents/Container";
import { formatCurrency } from "../../Utils/formatUtils";
import { notifyUser } from "../../Utils/notifyUser";

const BlackBaudDetailsPage = () => {
  const location = useLocation();
  const userDetails = useSelector((state: RootState) => state.auth);
  const [transactionDate] = useState<any>(location?.state?.rowData);
  const [expanded, setExpanded] = useState<string | false>(false);
  const [batchDetails, setBatchDetails] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const { t } = useTranslation();
  const translate = t as (key: string) => string;
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [expandedMessages, setExpandedMessages] = useState<{
    [key: string]: boolean;
  }>({});
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const [openLogs, setOpenLogs] = useState<{ [key: string]: boolean }>({});
  const [openReconciliation, setOpenReconciliation] = useState<{ [key: string]: boolean }>({});

  // ✅ Format date to MM/DD/YYYY HH:MM:SS
  const formatDateTime = (dateString: string) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "-";
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const seconds = String(date.getSeconds()).padStart(2, "0");
    return `${month}/${day}/${year} ${hours}:${minutes}:${seconds}`;
  };

  // ✅ Toggle long message view
  const toggleMessageExpand = (key: string) => {
    setExpandedMessages((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  useEffect(() => {
    if (transactionDate) {
      fetchData(transactionDate);
    }
  }, [transactionDate]);

  const handleChange =
    (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpanded(isExpanded ? panel : false);
    };

  const handleProcess = async (transactionDate: string) => {
    setLoading(true);
    try {
      const response = await postBlackBaudTransaction(transactionDate);
      if (response && response.status === constants.STATUS_CODES.SUCCESS) {
        notifyUser(response.data.message, "success", translate("blackBaudTransactionDetails"));
      } else {
        console.error(translate("errorInPostingDailyTransactions"), response);
        notifyUser(response.data.message, "error", translate("blackBaudTransactionDetails"));
      }
    } catch (err: any) {
      notifyUser(err?.response?.data?.message || translate("anUnexpectedError"), 'error', translate("blackBaudTransactionDetails"));
    } finally {
      setLoading(false);
      window.history.back();
    }
  };

  const fetchData = async (transactionDate: string) => {
    setLoading(true);
    setErrorMsg(null);
    try {
      const response = await getBlackBaudDetailsBytransactionDateAndClientId(transactionDate, userDetails.clientId);
      const apiData = response?.data?.data || response?.data;

      if (!apiData || Object.keys(apiData).length === 0) {
        setErrorMsg(translate("noDataFoundForThisTransaction"));
        setBatchDetails(null);
        setLoading(false);
        return;
      }

      const transformed = {
        internalTransactionId: apiData?.internalId || "-",
        transactionDate: apiData?.transactionDate || "-",
        participantId: apiData?.participantId || "-",
        currentStatus: apiData?.externalBatchVOS?.[0]?.status || "Unknown",
        batches:
          apiData?.externalBatchVOS?.map((batch: any) => ({
            batchId: batch?.id || "-",
            status: batch?.status || "Unknown",
            createdTime: formatDateTime(batch?.createdTime),
            completedTime: formatDateTime(batch?.completedTime),
            retryCount: batch?.retryCount ?? 0,
            processId: batch?.processId || "-",
            errorMessage: batch.errorMessage || batch.errorMessage || "",
            fundBatches:
              (batch?.externalBatchItems || batch?.externalBatchItem || []).map((item: any) => ({
                time: formatDateTime(item?.createdTime),
                journalId: item?.journalId || "-",
                requestType: item?.itemStatus || item?.internalTable || "-",
                internalTable: item?.internalTable,
                internalId: item?.internalId,
                itemStatus: item?.itemStatus,
                participantName: item?.participantName,
                entityType: item?.entityType,
                transactionTypeCategoryDesc: item?.transactionTypeCategoryDesc || "-",
                amount: formatCurrency(item?.amount),
              })) || [],
            logs:
              batch?.blackBaudTransLogs?.map((log: any) => ({
                time: formatDateTime(log?.createdTime),
                requestType: log?.type || "-",
                message: log?.payload || "-",
              })) || [],
          })) || [],
      };

      setBatchDetails(transformed);

      // ✅ Auto-expand the latest batch accordion
      const sortedBatches = transformed.batches.sort(
        (a: any, b: any) =>
          new Date(b.createdTime).getTime() - new Date(a.createdTime).getTime()
      );
      if (sortedBatches.length > 0) {
        setExpanded(String(sortedBatches[0].batchId));
      }
    } catch (error) {
      console.error(translate("errorFetchingBatchDetails"), error);
      setErrorMsg(translate("failedToLoadDataPleaseTryAgainLater"));
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = (json: string, key: string) => {
    navigator.clipboard.writeText(json);
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 1500);
  };

  if (loading) {
    return (
      <Box sx={{ textAlign: "center", mt: 5 }}>
        <CircularProgress />
        <Typography mt={2}>{translate("loadingDetails")}</Typography>
      </Box>
    );
  }

  if (errorMsg) {
    return (
      <Box sx={{ textAlign: "center", mt: 5 }}>
        <Typography color="error" variant="h6">
          {errorMsg}
        </Typography>
      </Box>
    );
  }

  if (!batchDetails) {
    return (
      <Box sx={{ textAlign: "center", mt: 5 }}>
        <Typography>{translate("noDataAvailable")}</Typography>
      </Box>
    );
  }

  return (
    <StyledParentContainer>
      <StyledOneChildContainer>
        <StyledTwoChildContainer sx={{ border: "none" }}>
          <SectionTitle>{translate("blackBaudTransactionDetails")}</SectionTitle>

          {/* Header Info */}
          <Box
            sx={{
              mt: 3,
              mb: 2,
              p: 2,
              bgcolor: "#f8f9fa",
              borderRadius: 2,
              boxShadow: 1,
            }}
          >
            <Typography>
              Current Status:{" "}
              <Chip
                label={batchDetails.currentStatus}
                color={
                  batchDetails.currentStatus === constants.API_RESPONSE_TYPE_SUCCESS
                    ? "success"
                    : batchDetails.currentStatus === constants.API_RESPONSE_TYPE_FAILED
                      ? "error"
                      : "warning"
                }
              />
            </Typography>
          </Box>

          {/* Accordions for each batch */}
          {batchDetails?.batches && batchDetails.batches.length > 0 ? (
            (() => {
              const sortedBatches = [...batchDetails.batches].sort(
                (a, b) =>
                  new Date(b.createdTime).getTime() -
                  new Date(a.createdTime).getTime()
              );
              const latestBatch = sortedBatches[0];

              return sortedBatches.map((batch: any) => (
                <Accordion
                  key={batch.batchId}
                  expanded={expanded === String(batch.batchId)}
                  onChange={handleChange(String(batch.batchId))}
                  sx={{
                    mb: 2,
                    borderLeft: `5px solid ${batch.status === constants.API_RESPONSE_TYPE_SUCCESS
                      ? "#2e7d32"
                      : batch.status === constants.API_RESPONSE_TYPE_FAILED
                        ? "#d32f2f"
                        : "#ed6c02"
                      }`,
                  }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        width: "100%",
                      }}
                    >
                      <Typography fontWeight={600}>
                        Batch ID: {batch.batchId}
                      </Typography>
                      <Box sx={{ textAlign: "right" }}>
                        <Typography variant="body2">
                          Status:{" "}
                          <Chip
                            label={batch.status}
                            color={
                              batch.status === constants.API_RESPONSE_TYPE_SUCCESS
                                ? "success"
                                : batch.status === constants.API_RESPONSE_TYPE_FAILED
                                  ? "error"
                                  : "warning"
                            }
                            size="small"
                          />
                        </Typography>
                        <Typography variant="body2">
                          Created: {batch.createdTime}
                        </Typography>
                      </Box>
                    </Box>
                  </AccordionSummary>

                  <AccordionDetails>
                    <Box
                      sx={{
                        mb: 2,
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <Typography variant="subtitle2" fontWeight={600}>
                        Batch Details
                      </Typography>
                      {batch.batchId === latestBatch.batchId &&
                        batch.status === constants.API_RESPONSE_TYPE_FAILED && (
                          <Button
                            variant="contained"
                            color="error"
                            size="small"
                            onClick={() =>
                              handleProcess(
                                batchDetails.transactionDate
                              )
                            }
                          >
                            {translate("reprocess")}
                          </Button>
                        )}
                    </Box>

                    {/* Batch Details Table */}
                    <TableContainer component={Paper} sx={{ mt: 1 }}>
                      <Table size="small">
                        <TableBody>
                          <TableRow>
                            <TableCell>{translate("batchId")}</TableCell>
                            <TableCell>{batch.batchId}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>{translate("createdTime")}</TableCell>
                            <TableCell>{batch.createdTime}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>{translate("completedTime")}</TableCell>
                            <TableCell>{batch.completedTime}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>Retry Count</TableCell>
                            <TableCell>{batch.retryCount}</TableCell>
                          </TableRow>
                          {batch.status === constants.API_RESPONSE_TYPE_SUBMISSION_FAILED && (
                            <TableRow>
                              <TableCell>Error Message</TableCell>
                              <TableCell>{batch.errorMessage}</TableCell>
                            </TableRow>
                          )}
                          <TableRow>
                            <TableCell>{translate("processId")}</TableCell>
                            <TableCell>{batch.processId}</TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>

                    {/* Transaction Logs Accordion */}
                    <Accordion
                      expanded={!!openLogs[batch.batchId]}
                      onChange={() =>
                        setOpenLogs((prev) => ({
                          ...prev,
                          [batch.batchId]: !prev[batch.batchId],
                        }))
                      }
                      sx={{ mb: 0, background: "#f8f9fa" }}
                    >
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}
                        className={openLogs[batch.batchId] ? basestyle.accordionHeader : ""}
                        style={!openLogs[batch.batchId] ? { background: "#E5F2F4" } : {}}>
                        <Typography variant="subtitle2" fontWeight={600}>
                          {translate("transactionLogs")}
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        {batch.logs && batch.logs.length > 0 ? (
                          <TableContainer component={Paper} sx={{ mt: 1 }}>
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell>{translate("timeStamp")}</TableCell>
                                  <TableCell>{translate("requestType")}</TableCell>
                                  <TableCell>{translate("message")}</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {batch.logs.map((log: any, i: number) => (
                                  <TableRow key={i}>
                                    <TableCell>{log.time}</TableCell>
                                    <TableCell>
                                      <Chip
                                        label={log.requestType}
                                        size="small"
                                        sx={{ width: 80 }}
                                      />
                                    </TableCell>
                                    <TableCell
                                      sx={{
                                        whiteSpace: "pre-wrap",
                                        wordBreak: "break-word",
                                        maxWidth: "450",
                                        p: 0,
                                        position: "relative",
                                      }}
                                    >
                                      {log.message.length > 200 ? (
                                        <>
                                          {expandedMessages[`${batch.batchId}-${i}`]
                                            ? (
                                              <Box
                                                sx={{
                                                  background: "#f5f5f5",
                                                  p: 1,
                                                  borderRadius: "4px",
                                                  maxWidth: "auto",
                                                  maxHeight: 300,
                                                  overflow: "auto",
                                                  fontFamily: "monospace",
                                                  fontSize: "13px",
                                                  position: "relative",
                                                }}
                                              >
                                                {/* Copy IconButton with Tooltip */}
                                                <Tooltip title={copiedKey === `${batch.batchId}-${i}` ? "Copied!" : "Copy JSON"} arrow>
                                                  <IconButton
                                                    onClick={() => handleCopy(JSON.stringify(log, null, 2), `${batch.batchId}-${i}`)}
                                                    size="small"
                                                    sx={{
                                                      position: "absolute",
                                                      right: 8,
                                                      top: 8,
                                                      zIndex: 2,
                                                      color: copiedKey === `${batch.batchId}-${i}` ? "success.main" : "text.secondary",
                                                      bgcolor: copiedKey === `${batch.batchId}-${i}` ? "#e7fce7" : "transparent",
                                                      transition: "color 0.2s, bgcolor 0.2s",
                                                    }}
                                                  >
                                                    {copiedKey === `${batch.batchId}-${i}` ? (
                                                      <CheckCircleIcon fontSize="small" />
                                                    ) : (
                                                      <CopyAllIcon fontSize="small" />
                                                    )}
                                                  </IconButton>
                                                </Tooltip>
                                                {JSON.stringify(log, null, 2)}
                                              </Box>
                                            )
                                            : (
                                              <>
                                                {log.message.slice(0, 200)}...{" "}
                                              </>
                                            )}
                                          <Button
                                            variant="text"
                                            size="small"
                                            onClick={() => toggleMessageExpand(`${batch.batchId}-${i}`)}
                                            sx={{
                                              textTransform: "none",
                                              color: "#1976d2",
                                              fontWeight: 500,
                                              ml: 1,
                                            }}
                                          >
                                            {expandedMessages[`${batch.batchId}-${i}`] ? "Show Less" : "Show More"}
                                          </Button>
                                        </>
                                      ) : (
                                        log.message
                                      )}
                                    </TableCell>

                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        ) : (
                          <Typography sx={{ mt: 1 }}>
                            {translate("noLogsAvailable")}
                          </Typography>
                        )}
                      </AccordionDetails>
                    </Accordion>

                    {/* Reconciliation Data Accordion */}
                    <Accordion
                      expanded={!!openReconciliation[batch.batchId]}
                      onChange={() =>
                        setOpenReconciliation((prev) => ({
                          ...prev,
                          [batch.batchId]: !prev[batch.batchId],
                        }))
                      }
                      sx={{ mb: 0, background: "#f8f9fa" }}
                    >
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}
                        className={openReconciliation[batch.batchId] ? basestyle.accordionHeader : ""}
                        style={!openReconciliation[batch.batchId] ? { background: "#E5F2F4" } : {}}>
                        <Typography variant="subtitle2" fontWeight={600}>
                          {translate("reconciliationData")}
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        {batch.fundBatches && batch.fundBatches.length > 0 ? (
                          <TableContainer component={Paper} sx={{ mt: 1 }}>
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell sx={{ textAlign: "center" }}>{translate("internalId")}</TableCell>
                                  <TableCell sx={{ textAlign: "left" }}>{translate("participantName")}</TableCell>
                                  <TableCell sx={{ textAlign: "left" }}>{translate("entityType")}</TableCell> 
                                  <TableCell sx={{ textAlign: "left" }}>{translate("transactionType")}</TableCell>
                                  <TableCell sx={{ textAlign: "right" }}>{translate("amount")}</TableCell> 
                                  <TableCell sx={{ textAlign: "center" }}>{translate("journalId")}</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {batch.fundBatches.map((log: any, i: number) => (
                                  <TableRow key={i}>
                                    <TableCell sx={{ textAlign: "center" }}>{log.internalId}</TableCell>
                                    <TableCell sx={{ textAlign: "left" }}>{log.participantName}</TableCell>
                                    <TableCell sx={{ textAlign: "left" }}>{log.entityType}</TableCell>
                                    <TableCell sx={{ textAlign: "left" }}>{log.transactionTypeCategoryDesc}</TableCell>
                                    <TableCell sx={{ textAlign: "right" }}>{log.amount}</TableCell> 
                                    <TableCell sx={{ textAlign: "center" }}>{log.journalId}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        ) : (
                          <Typography sx={{ mt: 1 }}>
                            {translate("noLogsAvailable")}
                          </Typography>
                        )}
                      </AccordionDetails>
                    </Accordion>
                  </AccordionDetails>
                </Accordion>
              ));
            })()
          ) : (
            <Typography sx={{ mt: 3 }}>{translate("noBatchesAvailable")}</Typography>
          )}

          <ContainedButton onClick={() => window.history.back()} sx={{ mt: 3 }}>
            {translate("back")}
          </ContainedButton>
        </StyledTwoChildContainer>
      </StyledOneChildContainer>
    </StyledParentContainer>
  );
};

export default BlackBaudDetailsPage;
